// The editable plan for a new agent (or an existing one, with agentId). Deploy/Save sends
// exactly what the card shows; the server validates everything again.
//
// Schemas: the picker offers only what the server said Deploy takes for the chosen type
// (plan.allowed_by_type[type]; plan.allowed_schemas for the plan's own type), never every schema. A
// type change re-filters it. When the card is tied to a patient, that chart's schema stays picked.
// Errors are the server's own sentences, shown as they come (a code becomes a sentence first:
// agentErrorText). Whatever holds Deploy back is said in words next to it. An agent being edited is
// held back on its schedule only when the trigger has been touched (an untouched one isn't sent).
//
// Auto: when the clinic's agent settings turn it off for the viewer (plan.policy.auto === false;
// docs/superpowers/specs/2026-10-15-agent-permissions-design.md), the Auto radio is held back with
// NO_AUTO, and a new agent starts in Ask. An agent being edited shows the mode it has: an untouched
// mode isn't sent, so its stored Auto stays (it runs as Ask, and the note says so: RUNS_AS_ASK), and
// it can only be moved to Ask.
//
// Demo: a new agent that doesn't run at once (a schedule, an event, a watch) offers "Do a demo run
// now", on by default: Deploy then asks the server for one run at once that changes and sends
// nothing (runner.is_demo), so the clinician sees how it will work and can tell it what to change.
import React, { useId, useRef, useState } from 'react';
import TriggerEditor from './TriggerEditor';
import { canonical, draftHint, isObject, text, textProblem } from './triggerRules';
import { agentErrorCanRetry, agentErrorText } from './agentErrors';
import { createAgent, updateAgent } from './agentsService';

const FIELD = 'w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-800 dark:text-slate-100';
const LABEL = 'block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1';
const BUTTON = 'rounded-md px-3 py-1.5 text-xs font-semibold';
const CHIP = 'px-2 py-0.5 rounded-md text-[11px] font-semibold';
const CHIP_ON = 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300';
const CHIP_OFF = 'bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400';
const LOCKED = 'opacity-60 cursor-not-allowed';
const WARN = 'text-[11px] text-amber-700 dark:text-amber-300';
const NOTE = 'text-[11px] text-slate-600 dark:text-slate-400';
const NONE_FIT = 'None of your schemas fit this kind of agent.';
const NEEDS_CHART = "Only an agent made from a patient's chart can start on that patient's reports.";
const STALE_TRIGGER = 'This schedule is no longer accepted: change it before the agent can run again.';
// The planner's note says the same words: the card says them once, by the radio.
const NO_AUTO = "Your clinic's agent settings don't allow Auto mode.";
const RUNS_AS_ASK = 'It runs as Ask.';
const MAX_TITLE = 200;
const MAX_INSTRUCTION = 4000;

const lower = (name) => String(name).toLowerCase();
// Schema names compare case-blind, as the server does; the list's own spelling is the one kept.
const has = (list, name) => list.some((item) => lower(item) === lower(name));
const spelled = (name, list) => list.find((item) => lower(item) === lower(name)) ?? name;
const sameNames = (a, b) => {
  const [left, right] = [new Set(a.map(lower)), new Set(b.map(lower))];
  return left.size === right.size && [...left].every((name) => right.has(name));
};
const sentence = (words) => (/[.!?]$/.test(words) ? words : `${words}.`);

export default function PlanCard({ plan, types = [], patient = null, agentId = null, onCancel, onDeployed }) {
  const p = plan?.plan || {};
  const editing = !!agentId;
  const autoOff = plan?.policy?.auto === false;
  const ids = useId();
  const [modeName, blockerId, lockId, emailHintId, noAutoId, demoHintId] = ['mode', 'blocker', 'lock', 'email', 'noauto', 'demo']
    .map((part) => `${part}-${ids}`);
  const inFlight = useRef(false);

  // What the schemas of a type are. The plan's lists only: with no list for a type, nothing fits it.
  const allowedFor = (key) => {
    const byType = plan?.allowed_by_type;
    if (isObject(byType) && Array.isArray(byType[key])) return byType[key];
    return key === p.type && Array.isArray(plan?.allowed_schemas) ? plan.allowed_schemas : [];
  };
  const labelOf = (key) => types.find((item) => item.key === key)?.label || key;
  const plainTitle = (key) => `${labelOf(key)} agent`;
  // The chart's schema as the type's list spells it, or null when the type can't take it.
  const patientSchema = patient && typeof patient.schema === 'string' ? patient.schema.trim() : '';
  const chartSchema = (list) => (patient && patientSchema && has(list, patientSchema) ? spelled(patientSchema, list) : null);

  // What the card opened with. Save compares against this, so a refresh behind the card can't
  // turn a field nobody touched into a change.
  const [base] = useState(() => ({
    title: text(p.title).trim(),
    task: text(p.task).trim(),
    mode: p.mode === 'auto' ? 'auto' : 'ask',
    trigger: isObject(p.trigger) && Object.keys(p.trigger).length ? p.trigger : { kind: 'now' },
    schemas: Array.isArray(p.schemas) ? p.schemas.filter((name) => typeof name === 'string') : [],
    timezone: p.timezone,
  }));
  const [title, setTitle] = useState(text(p.title));
  const [task, setTask] = useState(text(p.task));
  const [type, setType] = useState(p.type || 'general');
  const [mode, setMode] = useState(autoOff && !editing ? 'ask' : base.mode);
  const [trigger, setTrigger] = useState(base.trigger);
  const [schemas, setSchemas] = useState(() => {
    const room = allowedFor(p.type || 'general');
    const picked = [];
    base.schemas.forEach((name) => { if (!has(picked, name)) picked.push(spelled(name, room)); });
    const chart = chartSchema(room);
    return chart && !has(picked, chart) ? [chart, ...picked] : picked;
  });
  const [schemaNote, setSchemaNote] = useState(null);
  const [email, setEmail] = useState(p.email === true);
  // A demo run at once, for an agent that doesn't run at once (views: create's `demo`): on by
  // default, so a schedule is seen working before its first real run.
  const [demo, setDemo] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false); // the server has it: nothing more to send from this card
  const [error, setError] = useState(null);

  const allowed = allowedFor(type);
  const chart = chartSchema(allowed);
  const noneFit = !editing && allowed.length === 0;
  const patientProblem = !editing && patient && allowed.length > 0 && chart === null
    ? `This patient's schema isn't available for the ${labelOf(type)} type. Pick another type, or cancel and plan it again without "This patient".`
    : null;
  // "Only for this patient" needs a chart to be tied to. (An agent being edited may have one the
  // card doesn't know of: the server answers.)
  const needsChart = !editing && !patient && trigger?.kind === 'event' && trigger.event === 'new_report' && trigger.this_patient === true;
  // The picker lists what the type takes, and whatever is already picked, so nothing picked is hidden.
  const chips = [...allowed, ...schemas.filter((name) => !has(allowed, name))];
  // An agent being edited may keep its schemas as they are; one that is changed to none is refused.
  const schemasChanged = !sameNames(schemas, base.schemas);
  const needsSchema = schemas.length === 0 && !noneFit && (!editing || schemasChanged);
  const titleProblem = textProblem(title, 'title');
  const taskProblem = textProblem(task, 'instruction');
  const typeOptions = types.some((item) => item.key === type) ? types : [...types, { key: type, label: labelOf(type) }];
  const locked = (schema) => chart !== null && has(schemas, schema) && lower(chart) === lower(schema);
  const chartLocked = chips.some(locked);

  // An agent being edited keeps the schedule it has until its trigger is touched: the card sends a
  // trigger only when it changed, and the server doesn't look at one that isn't sent. So a stored
  // schedule that would be refused today (or that the card has no words for) doesn't hold back a
  // rename; a note says so instead, as the scheduler has paused such an agent. A trigger that was
  // touched must pass like a new one's. (Put back as it was, it is untouched again.)
  const triggerHint = draftHint(trigger);
  const triggerKept = editing && canonical(trigger) === canonical(base.trigger);
  const staleTrigger = triggerKept && triggerHint !== null;

  // The first thing that holds Deploy back, or null.
  const blocker = (!task.trim() && 'Say what the agent should do.') || titleProblem || taskProblem
    || (!triggerKept && triggerHint) || (noneFit && NONE_FIT) || patientProblem || (needsChart && NEEDS_CHART)
    || (needsSchema && 'Pick at least one schema.') || null;

  const toggleSchema = (schema) => {
    if (locked(schema)) return; // the chart's schema stays on
    setSchemas((now) => (has(now, schema) ? now.filter((name) => lower(name) !== lower(schema)) : [...now, schema]));
  };

  // A new type has its own schemas: keep the picks it takes, drop the rest and say so, and never
  // leave the card with none picked when the type takes some. A title that is still the plain
  // "<Type> agent" follows the type.
  const changeType = (next) => {
    if (next === type) return;
    const room = allowedFor(next);
    const kept = [];
    schemas.forEach((name) => { if (has(room, name) && !has(kept, name)) kept.push(spelled(name, room)); });
    const dropped = schemas.filter((name) => !has(room, name));
    let picked = kept;
    const home = chartSchema(room);
    if (home && !has(picked, home)) picked = [home, ...picked];
    const instead = !picked.length && room.length ? room[0] : null;
    if (instead) picked = [instead];
    setSchemas(picked);
    if (dropped.length && room.length) {
      const one = dropped.length === 1;
      setSchemaNote(`${dropped.join(', ')} can't be used with the ${labelOf(next)} type, so ${one ? "it's" : "they're"} left out.`
        + `${instead ? ` ${instead} is picked instead.` : ''}`);
    } else {
      setSchemaNote(null);
    }
    if (title === plainTitle(type)) setTitle(plainTitle(next));
    setType(next);
  };

  // Edit mode sends only what changed: the server re-validates any trigger it gets (and a changed
  // trigger starts a new schedule), and a finished one-off's date is in the past.
  const changes = () => {
    const patch = {};
    if (title.trim() !== base.title) patch.title = title.trim();
    if (task.trim() !== base.task) patch.instruction = task.trim();
    if (mode !== base.mode) patch.mode = mode;
    if (canonical(trigger) !== canonical(base.trigger)) {
      patch.trigger = trigger;
      if (base.timezone) patch.timezone = base.timezone;
    }
    if (schemasChanged) patch.schemas = schemas;
    return patch;
  };

  const save = async () => {
    if (inFlight.current || saved || blocker) return;
    inFlight.current = true;
    setSaving(true);
    setError(null);
    let agent = null;
    let wrote = false;
    let done = false;
    try {
      if (editing) {
        const patch = changes();
        if (Object.keys(patch).length) {
          agent = (await updateAgent(agentId, patch))?.agent;
          wrote = true;
        }
      } else {
        agent = (await createAgent({
          title: title.trim(), instruction: task.trim(), type, mode, trigger, email, timezone: p.timezone,
          demo: trigger?.kind !== 'now' && demo,
          scope: { schemas, patient: chart ? { schema: chart, id: String(patient.id) } : null },
        }))?.agent;
        wrote = true;
      }
      done = true;
    } catch (err) {
      setError({ message: agentErrorText(err), retry: agentErrorCanRetry(err) });
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
    if (!done) return;
    // Once the server has it, this card can't send it again, whatever the caller then does: a
    // callback that throws must not leave a Deploy that makes a second agent.
    if (wrote) setSaved(true);
    try {
      onDeployed?.(agent);
    } catch {
      setError({
        message: `${editing ? 'Your changes were saved' : 'The agent was created'}, but this window didn't update. Close it and look in your agents list.`,
        retry: false,
      });
    }
  };

  return (
    <div className="m-3 rounded-xl border border-blue-200 dark:border-blue-500/30 bg-blue-50/40 dark:bg-blue-500/[0.06] p-3 space-y-3">
      {(plan?.notes || []).filter((note) => !(autoOff && note === NO_AUTO)).map((note, index) => (
        <div key={index} className="text-[11px] text-amber-700 dark:text-amber-300">{note}</div>
      ))}
      <label className="block"><span className={LABEL}>Title</span>
        <input className={FIELD} maxLength={MAX_TITLE} placeholder={editing ? undefined : plainTitle(type)}
               value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      {titleProblem && <div className={WARN}>{titleProblem}</div>}
      <label className="block"><span className={LABEL}>What it does</span>
        <textarea rows={3} className={FIELD} maxLength={MAX_INSTRUCTION} value={task} onChange={(e) => setTask(e.target.value)} />
      </label>
      {taskProblem && <div className={WARN}>{taskProblem}</div>}
      {!editing && (
        <label className="block"><span className={LABEL}>Type</span>
          <select className={FIELD} value={type} onChange={(e) => changeType(e.target.value)}>
            {typeOptions.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select>
        </label>
      )}
      <TriggerEditor value={trigger} onChange={setTrigger} timezone={p.timezone}
                     hasPatient={!!patient || (editing && base.trigger.this_patient === true)}
                     allowNow={!editing || base.trigger.kind === 'now'} />
      {staleTrigger && <div className={WARN}>{STALE_TRIGGER}</div>}
      <div>
        <span className={LABEL}>Schemas</span>
        {noneFit ? (
          <div className={WARN}>{NONE_FIT}</div>
        ) : (
          <div role="group" aria-label="Schemas" className="flex flex-wrap gap-1">
            {chips.map((schema) => {
              const on = has(schemas, schema);
              const stays = locked(schema);
              return (
                <button key={schema} type="button" aria-pressed={on} aria-disabled={stays || undefined}
                        aria-describedby={stays ? lockId : undefined} onClick={() => toggleSchema(schema)}
                        className={`${CHIP} ${on ? CHIP_ON : CHIP_OFF} ${stays ? LOCKED : ''}`}>
                  {schema}
                </button>
              );
            })}
          </div>
        )}
        {chartLocked && <div id={lockId} className={`mt-1 ${NOTE}`}>{"This patient's schema stays on: the agent is tied to this patient."}</div>}
        {/* A live region is mounted before it has anything to say, so a screen reader hears the change. */}
        <div role="status" aria-label="Schema changes" className={schemaNote ? `mt-1 ${NOTE}` : undefined}>{schemaNote}</div>
        {patientProblem && <div className={`mt-1 ${WARN}`}>{patientProblem}</div>}
        {needsSchema && <div className={`mt-1 ${WARN}`}>Pick at least one schema.</div>}
      </div>
      {patient && (
        <div className={NOTE}>
          {patient.label ? `Linked to the chart of ${patient.label}.` : "Linked to this patient's chart."}
        </div>
      )}
      <div role="radiogroup" aria-label="Mode" className="space-y-1 text-[11px] text-slate-700 dark:text-slate-300">
        <label className="flex items-start gap-1.5">
          <input type="radio" name={modeName} checked={mode === 'ask'} onChange={() => setMode('ask')} aria-label="Ask before changes" />
          <span><b>Ask</b>: reads freely, asks you before every change or send.</span>
        </label>
        <label className={`flex items-start gap-1.5 ${autoOff ? LOCKED : ''}`}>
          <input type="radio" name={modeName} checked={mode === 'auto'} onChange={() => setMode('auto')} aria-label="Auto"
                 disabled={autoOff} aria-describedby={autoOff ? noAutoId : undefined} />
          <span><b>Auto</b>: acts on its own; still asks before sending to anyone not on file.</span>
        </label>
        {autoOff && <div id={noAutoId} className={NOTE}>{editing && base.mode === 'auto' ? `${NO_AUTO} ${RUNS_AS_ASK}` : NO_AUTO}</div>}
      </div>
      {!editing && (
        <div>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} aria-describedby={emailHintId} />
            Email me when it finishes or needs me
          </label>
          <div id={emailHintId} className="ml-5 text-[11px] text-slate-500 dark:text-slate-400">No patient details in the email.</div>
        </div>
      )}
      {!editing && trigger?.kind !== 'now' && (
        <div>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} aria-describedby={demoHintId} />
            Do a demo run now
          </label>
          <div id={demoHintId} className="ml-5 text-[11px] text-slate-500 dark:text-slate-400">
            See how it will do the job: it changes and sends nothing, and you can tell it what to change.
          </div>
        </div>
      )}
      {error && (
        <div role="alert" className="flex items-center justify-between gap-2 text-xs text-red-600 dark:text-red-400">
          <span>{error.message}</span>
          {error.retry && (
            <button type="button" onClick={save} disabled={saving || !!blocker} title={blocker || undefined}
                    className={`${BUTTON} shrink-0 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10 disabled:opacity-50`}>
              Retry
            </button>
          )}
        </div>
      )}
      {blocker && <div id={blockerId} className={WARN}>{`Can't ${editing ? 'save' : 'deploy'} yet: ${sentence(blocker)}`}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={`${BUTTON} text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800`}>Cancel</button>
        <button type="button" disabled={saving || saved || !!blocker} aria-describedby={blocker ? blockerId : undefined} onClick={save}
                className={`${BUTTON} bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white`}>
          {editing ? 'Save' : 'Deploy'}
        </button>
      </div>
    </div>
  );
}
